/**
 * t695u: starting a one-off checklist. Only the template's buildings you can start it at are offered: one starts
 * straight away, several open a searchable list (no cut-off). Used by the Checklists page (header menu, Templates tab
 * and, t696u, the "Start a checklist" section on To do) on computers and phones.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search } from 'lucide-react';
import { checklistStartBuildings, filterBuildings, type ChecklistTemplate } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useLocationsWith } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Input, Modal } from '@/components/ui';
import { useWfLookups } from '@/features/workforce/common';

type Loc = { id: string; name: string };

/** The searchable building list (shared by the computer dialog and the phone sheet). */
export function BuildingChoiceList({ buildings, onPick, busy }: { buildings: Loc[]; onPick: (id: string) => void; busy?: string | null }) {
  const [q, setQ] = useState('');
  const shown = filterBuildings(buildings, q);
  return (
    <div className="space-y-2" data-testid="start-building-list">
      {buildings.length > 6 && <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input autoFocus type="search" width="full" className="pl-8" placeholder="Search buildings" aria-label="Search buildings" value={q} onChange={e => setQ(e.target.value)} data-testid="start-building-search" />
      </div>}
      <div className="scroll-thin max-h-80 overflow-y-auto">
        {shown.map(l => (
          <button key={l.id} type="button" disabled={Boolean(busy)} onClick={() => onPick(l.id)} data-testid="start-building"
            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-60 dark:hover:bg-slate-800">
            <Building2 className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate">{l.name}</span>
          </button>
        ))}
        {!shown.length && <p className="px-2 py-2 text-sm text-slate-500">No buildings match.</p>}
      </div>
    </div>
  );
}

/**
 * `start(t, prefer?)`: `prefer` (e.g. the page's building filter) is used when the checklist can start there.
 * Render `picker` once; `sheet` swaps the dialog for the phone's bottom sheet.
 */
export function useStartChecklist(sheet?: (p: { open: boolean; onClose: () => void; title: string; children: ReactNode }) => ReactNode) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const navigate = useNavigate();
  const { locations } = useWfLookups();
  const allowed = useLocationsWith('ops.checklist.schedule.manage');
  const [ask, setAsk] = useState<{ t: ChecklistTemplate; buildings: Loc[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const go = async (t: ChecklistTemplate, location_id: string) => {
    setBusy(location_id);
    try {
      const r = await api.ops.startChecklist(ws!, { template_id: t.id, location_id });
      void queryClient.invalidateQueries({ queryKey: ['ops', ws] });
      setAsk(null);
      navigate(`/checklists/run/${r.id}`);
    } catch (e) { toast(errorMessage(e), 'error'); }
    finally { setBusy(null); }
  };
  const buildingsFor = (t: ChecklistTemplate) => checklistStartBuildings(t, locations, allowed);
  const start = async (t: ChecklistTemplate, prefer?: string) => {
    const where = buildingsFor(t);
    if (prefer && where.some(l => l.id === prefer)) return go(t, prefer);
    if (!where.length) { toast('You can’t start this checklist at any of your buildings', 'error'); return; }
    if (where.length === 1) return go(t, where[0].id);
    setAsk({ t, buildings: where });
  };
  const title = ask ? `Start “${ask.t.name}” at…` : '';
  const list = ask ? <BuildingChoiceList buildings={ask.buildings} busy={busy} onPick={id => void go(ask.t, id)} /> : null;
  const close = () => setAsk(null);
  const picker = sheet ? sheet({ open: ask !== null, onClose: close, title, children: list })
    : <Modal open={ask !== null} onClose={close} title={title}>{list}</Modal>;
  return { start, picker, buildingsFor, busy };
}
